// services/dictionaryStorage.js

/**
 * مدیریت ذخیره‌سازی دیکشنری در IndexedDB
 * ساختار:
 * - دیتابیس: WordDictionaryDB
 * - جداول: en_fa, de_fa, fr_fa, it_fa, tr_fa
 * - هر رکورد: { word, data, timestamp }
 *
 * حجم کل دیتابیس مانیتور می‌شود و در صورت رسیدن به 130 مگابایت،
 * قدیمی‌ترین رکوردها پاک می‌شوند
 */

const DB_NAME = "WordDictionaryDB";
const DB_VERSION = 1;
const MAX_DB_SIZE = 130 * 1024 * 1024; // 130 مگابایت

// نام جداول برای هر زبان
const STORES = {
  en_fa: "en_fa",
  de_fa: "de_fa",
  fr_fa: "fr_fa",
  it_fa: "it_fa",
  tr_fa: "tr_fa",
};

let dbInstance = null;

/**
 * باز کردن دیتابیس
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("خطا در باز کردن دیتابیس:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // ایجاد جداول برای هر زبان
      Object.values(STORES).forEach((storeName) => {
        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, { keyPath: "word" });
          store.createIndex("timestamp", "timestamp", { unique: false });
        }
      });
    };
  });
};

/**
 * دریافت نام جدول بر اساس زبان‌ها
 */
const getStoreName = (fromLang, toLang) => {
  const key = `${fromLang}_${toLang}`;
  return STORES[key] || null;
};

/**
 * ذخیره ترجمه در دیتابیس (نسخه بهبود یافته)
 */
export const saveTranslationToDB = async (fromLang, toLang, word, data) => {
  try {
    const db = await openDB();
    const storeName = getStoreName(fromLang, toLang);

    if (!storeName) {
      console.warn(`زبان پشتیبانی نمی‌شود: ${fromLang} به ${toLang}`);
      return false;
    }

    const transaction = db.transaction([storeName], "readwrite");
    const store = transaction.objectStore(storeName);

    // ذخیره با timestamp فعلی
    const record = {
      word: word.toLowerCase().trim(),
      data: data, // کل داده رو ذخیره کن (شامل dict و enData)
      timestamp: Date.now(),
    };

    await new Promise((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // پس از ذخیره، حجم دیتابیس را چک کن
    await checkDBSizeAndClean();

    return true;
  } catch (error) {
    console.error("خطا در ذخیره ترجمه در دیتابیس:", error);
    return false;
  }
};

/**
 * دریافت ترجمه از دیتابیس (نسخه بهبود یافته با بررسی ساختار)
 */
export const getTranslationFromDB = async (fromLang, toLang, word) => {
  try {
    const db = await openDB();
    const storeName = getStoreName(fromLang, toLang);

    if (!storeName) {
      return null;
    }

    const transaction = db.transaction([storeName], "readonly");
    const store = transaction.objectStore(storeName);

    const normalizedWord = word.toLowerCase().trim();

    const result = await new Promise((resolve, reject) => {
      const request = store.get(normalizedWord);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (result) {
      return result.data;
    }

    return null;
  } catch (error) {
    console.error("خطا در دریافت ترجمه از دیتابیس:", error);
    return null;
  }
};

/**
 * محاسبه حجم دیتابیس
 */
const calculateDBSize = async () => {
  try {
    const db = await openDB();
    let totalSize = 0;

    // محاسبه حجم تمام جداول
    for (const storeName of Object.values(STORES)) {
      if (!db.objectStoreNames.contains(storeName)) continue;

      const transaction = db.transaction([storeName], "readonly");
      const store = transaction.objectStore(storeName);

      const records = await new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // محاسبه حجم هر رکورد (تخمینی)
      records.forEach((record) => {
        const recordSize = new Blob([JSON.stringify(record)]).size;
        totalSize += recordSize;
      });
    }

    return totalSize;
  } catch (error) {
    console.error("خطا در محاسبه حجم دیتابیس:", error);
    return 0;
  }
};

/**
 * پاک کردن قدیمی‌ترین رکوردها از یک جدول خاص
 */
const cleanOldestRecords = async (storeName, count = 10) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([storeName], "readwrite");
    const store = transaction.objectStore(storeName);
    const index = store.index("timestamp");

    // دریافت قدیمی‌ترین رکوردها
    const oldestRecords = await new Promise((resolve, reject) => {
      const request = index.openCursor(null, "next"); // از قدیمی به جدید
      const records = [];
      let cursor = null;

      request.onsuccess = (event) => {
        cursor = event.target.result;
        if (cursor && records.length < count) {
          records.push({
            word: cursor.value.word,
            timestamp: cursor.value.timestamp,
          });
          cursor.continue();
        } else {
          resolve(records);
        }
      };
      request.onerror = () => reject(request.error);
    });

    // پاک کردن رکوردها
    for (const record of oldestRecords) {
      await new Promise((resolve, reject) => {
        const deleteRequest = store.delete(record.word);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
      });
    }

    return true;
  } catch (error) {
    console.error("خطا در پاک کردن رکوردهای قدیمی:", error);
    return false;
  }
};

/**
 * بررسی حجم دیتابیس و پاک کردن در صورت نیاز
 */
const checkDBSizeAndClean = async () => {
  try {
    const totalSize = await calculateDBSize();

    if (totalSize > MAX_DB_SIZE) {
      console.log(
        `حجم دیتابیس از حد مجاز گذشته: ${(totalSize / (1024 * 1024)).toFixed(
          2,
        )} مگابایت`,
      );

      // پاک کردن از همه جداول (از هر جدول 5 رکورد قدیمی)
      for (const storeName of Object.values(STORES)) {
        await cleanOldestRecords(storeName, 5);
      }

      // دوباره چک کن
      const newSize = await calculateDBSize();
      console.log(
        `حجم جدید دیتابیس: ${(newSize / (1024 * 1024)).toFixed(2)} مگابایت`,
      );
    }
  } catch (error) {
    console.error("خطا در بررسی حجم دیتابیس:", error);
  }
};

/**
 * پاک کردن کامل دیتابیس (در صورت نیاز)
 */
export const clearDictionaryDB = async () => {
  try {
    const db = await openDB();

    for (const storeName of Object.values(STORES)) {
      if (!db.objectStoreNames.contains(storeName)) continue;

      const transaction = db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }

    console.log("دیتابیس دیکشنری پاک شد");
    return true;
  } catch (error) {
    console.error("خطا در پاک کردن دیتابیس:", error);
    return false;
  }
};

export default {
  saveTranslationToDB,
  getTranslationFromDB,
  clearDictionaryDB,
};
